import {
  FormSlug,
  ImplementationGuideForm,
  LMSOption,
  NWEAIntentOption,
  PortalUsedOption,
  PrimaryIntegrationOption,
  ResponseFormat,
} from "@/components/ImplementationGuide/types";
import { FieldName } from "@/components/ImplementationGuide/types/FieldName.enum";

const integrationsForm: ImplementationGuideForm = {
  title: "Integrations",
  subtitle: "Ensure teachers and students log in and access Newsela easily.",
  slug: FormSlug.Integrations,
  form_image: "/images/implementation-guide/Integrations.svg",
  form_index: 1,
  form_description: {
    content:
      "We will verify information regarding your district's integration needs for accessing Newsela.",
    recommendation: "",
  },
  questions: [
    {
      question_index: 1,
      question_text:
        "What are the email domains that teachers/students will using to log in to Newsela?",
      sub_prompt: null,
      response_format: ResponseFormat.textarea,
      response_fields: {
        textarea_title: "List of all email domains",
        textarea_placeholder: "List of all email domains",
      },
      is_response_read_only: false,
      field_name: FieldName.Email_Domains,
    },
    {
      question_index: 2,
      question_text:
        "Please select which service you would like to use to provision and manage accounts and roster classes.",
      sub_prompt: null,
      response_format: ResponseFormat.radiobutton,
      response_fields: {
        textarea_title: "Name of service",
        textarea_placeholder: "Name of service",
        textarea_field_name: FieldName.Primary_Integration_Other,
        response_options: Object.values(PrimaryIntegrationOption),
      },
      is_response_read_only: false,
      field_name: FieldName.Primary_Integration,
      resources: [
        {
          response_option: PrimaryIntegrationOption.Clever,
          resource_name: "System Admin for Clever",
          resource_link:
            "https://support.newsela.com/article/clever-for-system-admins/",
          todo_item: "Complete System Admin for Clever",
        },
        {
          response_option: PrimaryIntegrationOption.Classlink,
          resource_name: "System Admin for Classlink",
          resource_link:
            "https://support.newsela.com/article/system-admin-instructions-for-classlink/",
          todo_item: "Complete System Admin for Classlink",
        },
      ],
    },
    {
      question_index: 3,
      question_text:
        "Please select how you would like students, teachers, and staff to log in",
      sub_prompt:
        "To avoid identity conflicts, please ensure that the email stored in your SSO provider are consistent with the emails provided by your rostering provider.",
      response_format: ResponseFormat.radiobutton,
      response_fields: {
        textarea_title: "Name of login portal",
        textarea_placeholder: "Name of login portal",
        textarea_field_name: FieldName.Portal_Used_Other,
        response_options: Object.values(PortalUsedOption),
      },
      is_response_read_only: false,
      field_name: FieldName.Portal_Used,
      resources: [
        {
          response_option: PortalUsedOption.Clever,
          resource_name: "Teacher Guide: Clever",
          resource_link:
            "https://go.newsela.com/rs/628-ZPE-510/images/Teacher_Getting_Setup_with%20Newsela_Clever.pdf",
          resource_link_text: "Open PDF",
          todo_item: "Shared Teacher Guide for Clever",
        },
        {
          response_option: PortalUsedOption.Classlink,
          resource_name: "Teacher Guide: Classlink",
          resource_link:
            "https://go.newsela.com/rs/628-ZPE-510/images/Teacher_Getting_Setup_with_Newsela_OneRoster_Classlink.pdf",
          resource_link_text: "Open PDF",
          todo_item: "Shared Teacher Guide for Classlink",
        },
        {
          response_option: PortalUsedOption.Google,
          resource_name: "Using Google for Teachers",
          resource_link:
            "https://support.newsela.com/article/using-google-classrooms-for-teachers/",
          todo_item: "Shared Teacher Guide for Google",
        },
        {
          response_option: PortalUsedOption.Google,
          resource_name: "Google Workspace Trusted Applications",
          resource_link:
            "https://support.newsela.com/article/add-newsela-as-a-trusted-app-in-google-workspace/",
          todo_item:
            "Setup Newsela as a Trusted Application in Google Workspace",
        },
        {
          response_option: PortalUsedOption.Canvas,
          resource_name: "Using Canvas for Teachers",
          resource_link:
            "https://support.newsela.com/article/teacher-instructions-for-canvas/",
          todo_item: "Shared Teacher Resource for Canvas",
        },
        {
          response_option: PortalUsedOption.Schoology,
          resource_name: "Using Schoology for Teachers",
          resource_link:
            "https://support.newsela.com/article/using-schoology-for-teachers/",
          todo_item: "Shared Teacher Resource for Schoology",
        },
        {
          response_option: PortalUsedOption.Microsoft,
          resource_name: "Microsoft Teams Setup",
          resource_link:
            "https://support.newsela.com/article/using-microsoft-for-teachers/",
          todo_item: "Shared Teacher Setup for Microsoft Teams",
        },
      ],
    },
    {
      question_index: 4,
      question_text:
        "Please select the LMS your district utilizes, if you choose to integrate with your LMS in addition to the rostering and SSO services you selected above.",
      sub_prompt: null,
      response_format: ResponseFormat.radiobutton,
      response_fields: {
        textarea_title: "Name of LMS",
        textarea_placeholder: "Name of LMS",
        textarea_field_name: FieldName.LMS_Other,
        response_options: Object.values(LMSOption),
      },
      is_response_read_only: false,
      field_name: FieldName.LMS,
      resources: [
        {
          response_option: LMSOption.GoogleClassroom,
          resource_name: "Google for System Admins",
          resource_link:
            "https://support.newsela.com/article/google-for-system-admins/",
          todo_item: "Complete System Admin for Google",
        },
        {
          response_option: LMSOption.Canvas,
          resource_name: "Canvas for System Admins",
          resource_link:
            "https://support.newsela.com/article/system-admin-instructions-for-canvas/",
          todo_item: "Complete System Admin for Canvas",
        },
        {
          response_option: LMSOption.Schoology,
          resource_name: "Schoology for System Admins",
          resource_link:
            "https://support.newsela.com/article/system-admin-instructions-for-schoology/",
          todo_item: "Complete System Admin for Schoology",
        },
        {
          response_option: LMSOption.D2LBrightspace,
          resource_name: "D2L for System Admins",
          resource_link:
            "https://support.newsela.com/article/d2l-brightspace-for-system-admins/",
          todo_item: "Complete System Admin for D2L",
        },
        {
          response_option: LMSOption.itslearning,
          resource_name: "ItsLearning for System Admins",
          resource_link:
            "https://support.newsela.com/article/system-admin-instructions-for-itslearning/",
          todo_item: "Complete System Admin for ItsLearning",
        },
      ],
    },
    {
      question_index: 5,
      question_text:
        "Does your district administer NWEA MAP assessments 3 times per year?  If yes, would your district like to sync NWEA MAP scores to Newsela?",
      sub_prompt: null,
      response_format: ResponseFormat.radiobutton,
      response_fields: {
        response_options: Object.values(NWEAIntentOption),
      },
      is_response_read_only: false,
      field_name: FieldName.NWEA_Intent,
      resources: [
        {
          response_option: NWEAIntentOption.YesAndSync,
          resource_name: "NWEA and Newsela",
          resource_link:
            "https://support.newsela.com/article/nwea-and-newsela/",
          todo_item: "Contact CSM or Support for NWEA Setup",
        },
      ],
    },
  ],
};

export default integrationsForm;
